import { CANVAS_WIDTH } from "../../game/constants";
import { isMobileViewport } from "../../game/platform";
import type { Rect, UiRects } from "../../game/input";
import type { RenderModel } from "../renderModel";
import {
  DAILY_LOGIN_REWARDS,
  DAILY_REWARDS_CYCLE,
  wrapToCycleDay,
} from "../../economy/dailyLoginRewards";
import { easeOutCubic } from "../../shared/easing";
import {
  GAME_FONT,
  MENU_TEXT_PRIMARY,
  uiCloseSize,
  UI_CLOSE_Y,
  UI_EDGE_PAD,
  menuGap,
} from "../renderConstants";

// ── Dependency interface ────────────────────────────────────────────

export interface DailyRewardsDeps {
  ctx: CanvasRenderingContext2D;
  canvasHeight: number;
  crateClosedImage: HTMLImageElement | null;
  crateOpenImage: HTMLImageElement | null;
  nowSec: () => number;
  drawCircleCloseButton: (rect: Rect) => void;
  drawCoinIcon: (x: number, y: number, size: number) => void;
  drawGemIcon: (x: number, y: number, size: number) => void;
  staggerAlpha: (index: number, delayMs: number, durationMs: number) => number;
}

// ── Extracted screen function ───────────────────────────────────────

export function drawDailyRewardsScreen(deps: DailyRewardsDeps, model: RenderModel): UiRects {
  const {
    ctx,
    canvasHeight: canvasH,
    crateClosedImage,
    crateOpenImage,
    nowSec,
    drawCircleCloseButton,
    drawCoinIcon,
    drawGemIcon,
    staggerAlpha,
  } = deps;
  const centerX = CANVAS_WIDTH / 2;
  const uiRects: UiRects = {};
  const status = model.dailyLoginStatus;
  const currentDay = status?.currentDay ?? 0;
  const cycleDay = wrapToCycleDay(currentDay);
  const canClaim = status?.canClaimToday === true;
  const claimableCycleDay = status?.claimableDay != null ? wrapToCycleDay(status.claimableDay) : null;

  // Close button
  const closeRect: Rect = { x: CANVAS_WIDTH - uiCloseSize() - UI_EDGE_PAD, y: UI_CLOSE_Y, w: uiCloseSize(), h: uiCloseSize() };
  drawCircleCloseButton(closeRect);
  uiRects.dailyRewardBack = closeRect;

  // Card grid: 3-3-1 (same layout as skins grid style)
  const gridW = CANVAS_WIDTH - UI_EDGE_PAD * 2;
  const gapX = menuGap();
  const gapY = menuGap();
  const cols = 3;
  const cardW = Math.floor((gridW - gapX * (cols - 1)) / cols);
  const cardH = cardW;
  const gridX = UI_EDGE_PAD;
  const day7H = Math.floor(cardH * 1.2);
  const headerH = 24; // title only
  const totalContentH = headerH + 2 * (cardH + gapY) + day7H;
  const topOffset = Math.max(8, (canvasH - totalContentH) / 2);

  // Title (matches SKINS / SETTINGS page title size)
  ctx.fillStyle = MENU_TEXT_PRIMARY;
  ctx.font = `bold 10px ${GAME_FONT}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("DAILY REWARDS", centerX, UI_CLOSE_Y + uiCloseSize() / 2);
  let curY = topOffset;

  const tierBorder: Record<string, string> = {
    normal: "#6f94c5",
    bigger: "#b44aff",
    substantial: "#ffd700",
  };
  for (let i = 0; i < DAILY_REWARDS_CYCLE; i++) {
    const reward = DAILY_LOGIN_REWARDS[i]!;
    const dayNum = i + 1;
    const isLastRow = dayNum === 7;
    const col = isLastRow ? 0 : i % cols;
    const row = isLastRow ? 2 : Math.floor(i / cols);
    const cw = isLastRow ? gridW : cardW;
    const ch = isLastRow ? Math.floor(cardH * 1.2) : cardH;
    const cx = isLastRow ? gridX : gridX + col * (cardW + gapX);
    const cy = curY + row * (cardH + gapY);

    const isClaimed = dayNum < cycleDay || (dayNum === cycleDay && !canClaim);
    const isClaimable = canClaim && dayNum === claimableCycleDay;
    const isLocked = !isClaimed && !isClaimable;

    // Stagger animation like skins grid
    const gridAlpha = staggerAlpha(i, 60, 250);
    if (gridAlpha <= 0) continue;
    if (gridAlpha < 1) {
      ctx.save();
      const scl = 0.8 + 0.2 * easeOutCubic(gridAlpha);
      const cardCX = cx + cw / 2;
      const cardCY = cy + ch / 2;
      ctx.globalAlpha = easeOutCubic(gridAlpha);
      ctx.translate(cardCX, cardCY);
      ctx.scale(scl, scl);
      ctx.translate(-cardCX, -cardCY);
    }

    // Crate image: fills the cell
    const crateCardImg = isClaimed ? crateOpenImage : crateClosedImage;
    const crateSz = isLastRow ? Math.floor(ch * 0.85) : Math.floor(cw * 0.95);
    const crateDrawX = cx + (cw - crateSz) / 2;
    const crateDrawY = cy + (ch - crateSz) / 2;

    // Desat + darken for claimed/locked (no alpha)
    ctx.save();
    if (isClaimed || isLocked) {
      ctx.filter = "grayscale(0.9) brightness(0.3)";
    }

    // Pulsing glow for claimable
    if (isClaimable) {
      const pulse = 0.5 + 0.5 * Math.sin(nowSec() * 3);
      ctx.shadowColor = tierBorder[reward.tier] ?? "#6f94c5";
      ctx.shadowBlur = 10 + pulse * 10;
    }

    if (crateCardImg != null) {
      ctx.drawImage(crateCardImg, crateDrawX, crateDrawY, crateSz, crateSz);
    }
    ctx.restore();

    // Day label above crate
    const mobile = isMobileViewport();
    const dayFontPx = mobile ? 10 : 6;
    ctx.fillStyle = "#ffffff";
    ctx.font = `bold ${dayFontPx}px ${GAME_FONT}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "bottom";
    ctx.fillText(`DAY ${dayNum}`, cx + cw / 2, crateDrawY + 2);

    // Rewards overlayed on center of crate (hide for claimed and claimable)
    if (!isClaimed && !isClaimable) {
      const rIconSz = 14;
      const rFontSz = 8;
      const iconGap = isLastRow ? 32 : 20;
      const rMidX = cx + cw / 2;
      const rMidY = cy + ch / 2;

      // Coin icon + number below
      const coinCenterX = rMidX - iconGap / 2;
      drawCoinIcon(coinCenterX - rIconSz / 2, rMidY - rIconSz / 2 - 2, rIconSz);
      ctx.fillStyle = "#ffd700";
      ctx.font = `bold ${rFontSz}px ${GAME_FONT}`;
      ctx.textAlign = "center";
      ctx.textBaseline = "top";
      ctx.fillText(`${reward.coins}`, coinCenterX, rMidY + rIconSz / 2);

      // Gem icon + number below
      const gemCenterX = rMidX + iconGap / 2;
      drawGemIcon(gemCenterX - rIconSz / 2, rMidY - rIconSz / 2 - 2, rIconSz);
      ctx.fillStyle = "#64f6ff";
      ctx.font = `bold ${rFontSz}px ${GAME_FONT}`;
      ctx.textAlign = "center";
      ctx.textBaseline = "top";
      ctx.fillText(`${reward.gems}`, gemCenterX, rMidY + rIconSz / 2);
    }

    ctx.globalAlpha = 1;
    if (gridAlpha < 1) ctx.restore();

    // Hit rect for claimable day only
    if (isClaimable) {
      const key = `dailyRewardDay${i}` as keyof UiRects;
      (uiRects as Record<string, Rect>)[key] = { x: cx, y: cy, w: cw, h: ch };
    }
  }

  return uiRects;
}
